"use client"

import { useState, useEffect, useCallback } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  X,
  Download,
  ExternalLink,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Minimize2,
  Heart,
  Share2,
  FileText,
  Video,
  ImageIcon,
  FileSpreadsheet,
  Music,
  Archive,
  RotateCcw,
  Loader2,
} from "lucide-react"
import dynamic from "next/dynamic"
import { downloadFile } from "@/utils"
import { getFileDownload } from "@/lib/actions/file-actions"

// 动态导入查看器组件以优化性能
const PdfViewer = dynamic(() => import("@/components/viewers/PdfViewer"), {
  loading: () => (
    <div className="flex items-center justify-center h-full">
      <Loader2 className="h-8 w-8 animate-spin" />
    </div>
  ),
  ssr: false,
})

const PlyrVideoViewer = dynamic(
  () => import("@/components/viewers/PlyrVideoViewer"),
  {
    loading: () => (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    ),
    ssr: false,
  }
)

const WordViewer = dynamic(() => import("@/components/viewers/WordViewer"), {
  loading: () => (
    <div className="flex items-center justify-center h-full">
      <Loader2 className="h-8 w-8 animate-spin" />
    </div>
  ),
  ssr: false,
})

interface FilePreviewModalProps {
  file: {
    id: string
    name: string
    type: string
    url: string
    size?: string
    downloadCount?: number
    isLoading?: boolean
  } | null
  isOpen: boolean
  onClose: () => void
}

export function FilePreviewModal({
  file,
  isOpen,
  onClose,
}: FilePreviewModalProps) {
  const [zoom, setZoom] = useState(100)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)

  // 下载处理函数
  const handleDownload = useCallback(async () => {
    if (!file) return
    try {
      const result = await getFileDownload(file.id)
      if (result.success && result.downloadUrl) {
        downloadFile(result.downloadUrl)
      } else {
        console.error("获取下载URL失败:", result.error)
      }
    } catch (error) {
      console.error("获取下载URL失败:", error)
    }
  }, [file])

  // 适合窗口大小
  const handleFitToWindow = useCallback(() => {
    const container = document.querySelector(
      "[data-preview-container]"
    ) as HTMLElement
    const img = container?.querySelector("img") as HTMLImageElement

    if (!container || !img) return

    const containerRect = container.getBoundingClientRect()
    const containerWidth = containerRect.width - 32
    const containerHeight = containerRect.height - 32

    const imgWidth = img.naturalWidth
    const imgHeight = img.naturalHeight

    const scaleX = (containerWidth * 0.85) / imgWidth
    const scaleY = (containerHeight * 0.85) / imgHeight
    const autoScale = Math.min(scaleX, scaleY)

    const finalScale = Math.max(0.3, Math.min(1, autoScale))
    setZoom(Math.round(finalScale * 100))
  }, [])

  // 键盘快捷键支持
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return

      switch (event.key) {
        case "Escape":
          onClose()
          break
        case " ":
          event.preventDefault()
          handleDownload()
          break
        case "+":
        case "=":
          if (isImageFile(file?.type)) {
            setZoom(prev => Math.min(300, prev + 25))
          }
          break
        case "-":
          if (isImageFile(file?.type)) {
            setZoom(prev => Math.max(25, prev - 25))
          }
          break
        case "0":
          if (isImageFile(file?.type)) {
            setZoom(100)
          }
          break
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [isOpen, file?.type, onClose, handleDownload])

  // 重置状态当文件改变时
  useEffect(() => {
    if (file) {
      setZoom(100)
      setImageLoaded(false)
    }
  }, [file])

  // 图片自动适配缩放
  const handleImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.target as HTMLImageElement

      const imgWidth = img.naturalWidth
      const imgHeight = img.naturalHeight

      // 获取实际的容器尺寸
      const container = img.closest("[data-preview-container]") as HTMLElement
      let containerWidth: number
      let containerHeight: number

      if (container) {
        const containerRect = container.getBoundingClientRect()
        containerWidth = containerRect.width - 32 // 减去padding
        containerHeight = containerRect.height - 32 // 减去padding
      } else {
        // 备用方案：使用视窗尺寸
        containerWidth = window.innerWidth * 0.8
        containerHeight = window.innerHeight * 0.7
      }

      // 计算适合容器的缩放比例，目标是让图片占据容器的85%
      const scaleX = (containerWidth * 0.85) / imgWidth
      const scaleY = (containerHeight * 0.85) / imgHeight
      const autoScale = Math.min(scaleX, scaleY)

      // 设置合理的缩放范围：30% - 100%，避免图片过小
      const finalScale = Math.max(0.3, Math.min(1, autoScale))

      console.log("图片自适应缩放:", {
        containerSize: { width: containerWidth, height: containerHeight },
        imageSize: { width: imgWidth, height: imgHeight },
        scaleX,
        scaleY,
        autoScale,
        finalScale,
        finalZoom: Math.round(finalScale * 100),
      })

      setZoom(Math.round(finalScale * 100))
      setImageLoaded(true)
    },
    []
  )

  if (!file) return null

  const isImageFile = (type?: string) => {
    return (
      type &&
      ["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(type.toLowerCase())
    )
  }

  const isPdfFile = (type?: string) => {
    return type?.toLowerCase() === "pdf"
  }

  const isVideoFile = (type?: string) => {
    return (
      type &&
      ["mp4", "avi", "mov", "wmv", "flv", "webm"].includes(type.toLowerCase())
    )
  }

  const isWordFile = (type?: string) => {
    return type && ["doc", "docx"].includes(type.toLowerCase())
  }

  const getFileIcon = (type: string) => {
    const lowerType = type.toLowerCase()

    if (isPdfFile(lowerType)) {
      return <FileText className="h-5 w-5 text-red-500" />
    }
    if (isVideoFile(lowerType)) {
      return <Video className="h-5 w-5 text-blue-500" />
    }
    if (isImageFile(lowerType)) {
      return <ImageIcon className="h-5 w-5 text-green-500" />
    }
    if (isWordFile(lowerType)) {
      return <FileText className="h-5 w-5 text-blue-600" />
    }
    if (["xls", "xlsx"].includes(lowerType)) {
      return <FileSpreadsheet className="h-5 w-5 text-green-600" />
    }
    if (["mp3", "wav", "flac", "aac"].includes(lowerType)) {
      return <Music className="h-5 w-5 text-purple-500" />
    }
    if (["zip", "rar", "7z", "tar", "gz"].includes(lowerType)) {
      return <Archive className="h-5 w-5 text-orange-500" />
    }

    return <FileText className="h-5 w-5 text-gray-500" />
  }

  const renderPreviewContent = () => {
    // 如果正在加载，显示加载状态
    if (file.isLoading || !file.url) {
      return (
        <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50 rounded-lg">
          <Loader2 className="h-12 w-12 animate-spin text-blue-500 mb-4" />
          <p className="text-gray-600 text-lg font-medium">正在加载预览...</p>
          <p className="text-gray-400 text-sm mt-2">请稍候，正在获取文件内容</p>
        </div>
      )
    }

    const fileType = file.type.toLowerCase()

    // PDF 文件使用专门的 PDF 查看器
    if (isPdfFile(fileType)) {
      return (
        <div className="w-full h-full bg-gray-50 rounded-lg overflow-hidden">
          <PdfViewer fileUrl={file.url} />
        </div>
      )
    }

    // 视频文件使用专门的视频查看器
    if (isVideoFile(fileType)) {
      return (
        <div className="w-full h-full bg-black rounded-lg overflow-hidden flex items-center justify-center">
          <PlyrVideoViewer
            src={file.url}
            className="w-full h-full max-w-full max-h-full"
          />
        </div>
      )
    }
    // Word 文件使用专门的 Word 查看器
    if (isWordFile(fileType)) {
      return (
        <div className="w-full h-full bg-white rounded-lg overflow-hidden">
          <ScrollArea className="h-full">
            <div className="p-4">
              <WordViewer fileUrl={file.url} />
            </div>
          </ScrollArea>
        </div>
      )
    }

    // 图片文件使用优化的图片查看器
    if (isImageFile(fileType)) {
      return (
        <div
          className="w-full h-full flex items-center justify-center bg-gray-100 rounded-lg overflow-hidden"
          data-preview-container
        >
          <div
            className="relative transition-transform duration-200 ease-in-out flex items-center justify-center"
            style={{
              transform: `scale(${zoom / 100})`,
              transformOrigin: "center center",
              maxWidth: "100%",
              maxHeight: "100%",
            }}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={file.url}
              alt={file.name}
              className="object-contain"
              style={{
                width: "auto",
                height: "auto",
                opacity: imageLoaded ? 1 : 0,
                transition: "opacity 0.3s ease-in-out",
              }}
              onLoad={handleImageLoad}
              onError={e => {
                const target = e.target as HTMLImageElement
                target.src = "/placeholder.svg"
                setImageLoaded(true)
              }}
            />
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            )}
          </div>
        </div>
      )
    }

    // 音频文件
    if (["mp3", "wav", "flac", "aac"].includes(fileType)) {
      return (
        <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50 rounded-lg">
          <Music className="h-16 w-16 mb-4 text-purple-500" />
          <h3 className="text-lg font-medium mb-4">{file.name}</h3>
          <audio controls className="w-full max-w-md">
            <source src={file.url} type={`audio/${fileType}`} />
            您的浏览器不支持音频播放
          </audio>
        </div>
      )
    }

    // 不支持预览的文件类型
    return (
      <div className="w-full h-full flex flex-col items-center justify-center text-gray-500 bg-gray-50 rounded-lg">
        {getFileIcon(file.type)}
        <div className="mt-4 text-center">
          <p className="text-lg font-medium mb-2">无法预览此文件类型</p>
          <p className="text-sm text-gray-400 mb-4">
            文件格式：{file.type.toUpperCase()}
          </p>
          <p className="text-sm mb-6">请下载文件查看完整内容</p>
          <Button
            onClick={handleDownload}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Download className="h-4 w-4 mr-2" />
            下载文件
          </Button>
        </div>
      </div>
    )
  }

  return (
    <TooltipProvider>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent
          className={`${
            isFullscreen
              ? "!max-w-screen !max-h-screen w-screen h-screen rounded-none"
              : "!max-w-[95vw] !max-h-[95vh] w-[95vw] sm:w-[90vw] md:w-[85vw] lg:w-[80vw] xl:w-[75vw]"
          } p-0 overflow-hidden flex flex-col gap-0`}
          style={{
            width: isFullscreen ? "100vw" : undefined,
            height: isFullscreen ? "100vh" : undefined,
          }}
        >
          {/* 头部工具栏 */}
          <DialogHeader className="flex flex-row items-center justify-between p-3 sm:p-4 border-b bg-white/95 backdrop-blur-sm flex-shrink-0 z-10">
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
              {getFileIcon(file.type)}
              <div className="min-w-0 flex-1">
                <DialogTitle className="text-base sm:text-lg font-semibold truncate">
                  {file.name}
                </DialogTitle>
                <div className="flex items-center space-x-1 sm:space-x-2 mt-1">
                  <Badge variant="secondary" className="text-xs">
                    {file.type.toUpperCase()}
                  </Badge>
                  {file.size && (
                    <span className="text-xs text-gray-500 hidden sm:inline">
                      {file.size}
                    </span>
                  )}
                  {file.downloadCount !== undefined && (
                    <span className="text-xs text-gray-500 hidden md:inline">
                      {file.downloadCount} 次下载
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-1 sm:space-x-2">
              {/* 图片缩放控制 */}
              {isImageFile(file.type) && (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setZoom(Math.max(25, zoom - 25))}
                        disabled={zoom <= 25}
                      >
                        <ZoomOut className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>缩小 (-)</TooltipContent>
                  </Tooltip>

                  <span className="text-sm text-gray-600 min-w-[40px] sm:min-w-[50px] text-center hidden sm:inline">
                    {zoom}%
                  </span>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setZoom(Math.min(300, zoom + 25))}
                        disabled={zoom >= 300}
                      >
                        <ZoomIn className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>放大 (+)</TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setZoom(100)}
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>重置缩放 (0)</TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleFitToWindow}
                      >
                        <Maximize2 className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>适合窗口</TooltipContent>
                  </Tooltip>

                  <Separator orientation="vertical" className="h-6" />
                </>
              )}

              {/* 操作按钮 */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    className="hidden md:flex"
                  >
                    <Heart className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>收藏</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    className="hidden md:flex"
                  >
                    <Share2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>分享</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleDownload}
                    className="hidden sm:flex"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>在新窗口打开</TooltipContent>
              </Tooltip>

              <Separator orientation="vertical" className="h-6" />

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setIsFullscreen(!isFullscreen)}
                  >
                    {isFullscreen ? (
                      <Minimize2 className="h-4 w-4" />
                    ) : (
                      <Maximize2 className="h-4 w-4" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {isFullscreen ? "退出全屏" : "全屏"}
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button size="sm" variant="outline" onClick={onClose}>
                    <X className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>关闭 (ESC)</TooltipContent>
              </Tooltip>
            </div>
          </DialogHeader>

          {/* 预览内容区域 */}
          <div
            className={`${
              isFullscreen
                ? "h-[calc(100vh-140px)]"
                : "h-[calc(95vh-140px)] min-h-[400px]"
            } overflow-hidden flex items-center justify-center flex-grow`}
          >
            <div className="w-full h-full">{renderPreviewContent()}</div>
          </div>

          {/* 底部信息栏 */}
          <div className="border-t bg-white/95 backdrop-blur-sm p-2 sm:p-3 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <Button
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={handleDownload}
                >
                  <Download className="h-4 w-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">下载文件</span>
                  <span className="sm:hidden">下载</span>
                </Button>
                <Button size="sm" variant="outline" className="hidden sm:flex">
                  <Heart className="h-4 w-4 mr-2" />
                  收藏
                </Button>
                <Button size="sm" variant="outline" className="hidden md:flex">
                  <Share2 className="h-4 w-4 mr-2" />
                  分享
                </Button>
              </div>
              <div className="text-xs text-gray-500 hidden lg:block">
                快捷键：ESC 关闭 • Space 下载 • +/- 缩放 • 0 重置
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  )
}
